import React, { useState, useEffect, useMemo } from 'react'
import { useFormik } from "formik";
import * as yup from 'yup';
import { formatDate } from '../../utils/dateUtils';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import ButtonLoading from '../ButtonLoading';
import Alert from '../common/Alert';
import { FiBriefcase, FiPhone, FiCreditCard, FiCalendar, FiActivity, FiType, FiInfo, FiCheckCircle } from 'react-icons/fi';
import { RiSchoolLine, RiMoneyDollarCircleLine } from 'react-icons/ri';

const phoneRules = /^(0)\d{10}$/;
const DEFAULT_BVN = '00000000000';
const DEFAULT_REG_TYPE = 'Private_Incorporated';
const DEFAULT_REG_DATE = '2024-01-01';

const SERVICES = [
    {
        id: 'school_management',
        label: 'School Management',
        description: 'Manage students, classes, results, attendance and more.',
        icon: RiSchoolLine,
    },
    {
        id: 'financial_management',
        label: 'Financial Management',
        description: 'Collect fees, manage payouts and track transactions.',
        icon: RiMoneyDollarCircleLine,
    },
];

const Information = ({ setRefresh, info, onSaveSuccess }) => {
    const { school } = useStateContext();
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState(['school_management']);

    const wantsFinancial = services.includes('financial_management');

    useEffect(() => {
        if (info) {
            formik.setValues({
                ...info,
                date_of_registration: formatDate(info.date_of_registration) || "",
            });
            if (info.services?.length) {
                setServices(info.services);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info]);

    const validationSchema = useMemo(() => {
        const base = {
            school_name: yup.string().required("School name is required"),
            phone_number: yup.string()
                .min(10, "Phone number must be at least 10 characters")
                .matches(phoneRules, { message: "Enter a valid phone number" })
                .required("Required"),
            industry: yup.string().required("School category is required"),
            description: yup.string().required("Please describe your school"),
        };

        if (wantsFinancial) {
            return yup.object().shape({
                ...base,
                bvn: yup.string()
                    .matches(/^[0-9]{11}$/, "BVN must be exactly 11 digits")
                    .required("Owner BVN is required"),
                date_of_registration: yup.date()
                    .typeError("Invalid date format")
                    .required("Date is required")
                    .max(new Date(), "Date cannot be in the future"),
                registration_type: yup.string().required("Registration type is required"),
            });
        }

        return yup.object().shape(base);
    }, [wantsFinancial]);

    const formik = useFormik({
        initialValues: {
            school_name: '',
            phone_number: '',
            bvn: '',
            date_of_registration: '',
            registration_type: '',
            industry: '',
            description: '',
            ...info,
        },
        validationSchema,
        onSubmit: (values) => {
            registerHandler(values);
        },
    });

    const toggleService = (id) => {
        if (id === 'school_management') return; // always required
        setServices(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const registerHandler = async (values) => {
        setLoading(true);
        try {
            const payload = {
                school_name: values.school_name,
                phone_number: values.phone_number,
                industry: values.industry,
                description: values.description,
                services,
                bvn: wantsFinancial ? values.bvn : (values.bvn || DEFAULT_BVN),
                registration_type: wantsFinancial ? values.registration_type : (values.registration_type || DEFAULT_REG_TYPE),
                date_of_registration: (() => {
                    const d = values.date_of_registration ? new Date(values.date_of_registration) : null;
                    const valid = d && !isNaN(d.getTime());
                    if (wantsFinancial) return valid ? formatDate(values.date_of_registration) : '';
                    return valid ? formatDate(values.date_of_registration) : DEFAULT_REG_DATE;
                })(),
            };

            await userRequest.put(`/schools/${school._id}`, payload);

            setSuccess("School information updated successfully");
            setTimeout(() => {
                setSuccess("");
                setRefresh("information");
                onSaveSuccess?.();
            }, 2000);
        } catch (error) {
            console.log(error);
            setError("Something went wrong. Please try again.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setLoading(false);
        }
    };

    const InfoItem = ({ icon: Icon, label, value }) => (
        <div className="flex items-start space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
            <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600">
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900 truncate dark:text-white">
                    {value || "-"}
                </p>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {label}
                </p>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Tell us about your school</h3>
                <p className="text-gray-500 max-w-2xl">
                    Help us set up your account by providing a few details about your school and the services you'd like to use.
                </p>
            </div>

            {/* Current details summary */}
            {info?.school_name && (
                <div className="mb-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-6 dark:bg-gray-800 dark:border-gray-700">
                    <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                        <span className="w-2 h-6 bg-indigo-500 rounded-full"></span>
                        Current Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <InfoItem icon={FiBriefcase} label="School Name" value={info.school_name} />
                        <InfoItem icon={FiPhone} label="School Phone" value={info.phone_number} />
                        <InfoItem icon={FiActivity} label="School Category" value={info.industry} />
                        {info.services?.includes('financial_management') && (
                            <>
                                <InfoItem icon={FiCreditCard} label="Owner's BVN" value={info.bvn} />
                                <InfoItem icon={FiCalendar} label="Registration Date" value={info.date_of_registration} />
                                <InfoItem icon={FiType} label="Registration Type" value={info.registration_type} />
                            </>
                        )}
                    </div>
                    {info.description && (
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <InfoItem icon={FiInfo} label="About School" value={info.description} />
                        </div>
                    )}
                </div>
            )}

            <div className="bg-white rounded-2xl">
                <h4 className="text-xl font-bold mb-6 text-gray-800">Update Information</h4>

                {success && <Alert type="success" message={success} />}
                {error && <Alert type="error" message={error} />}

                {/* Service selection */}
                <div className="mb-8">
                    <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Which services are you interested in?
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {SERVICES.map(({ id, label, description, icon: Icon }) => {
                            const selected = services.includes(id);
                            const locked = id === 'school_management';
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => toggleService(id)}
                                    className={`relative text-left rounded-2xl border-2 px-5 py-4 transition-all ${
                                        selected
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    } ${locked ? 'cursor-default' : 'cursor-pointer'}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-xl ${selected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className={`text-sm font-semibold ${selected ? 'text-indigo-700' : 'text-gray-700'}`}>
                                                {label}
                                                {locked && <span className="ml-2 text-xs font-normal text-gray-400">(required)</span>}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                                        </div>
                                    </div>
                                    {selected && (
                                        <FiCheckCircle className="absolute top-3 right-3 text-indigo-500 w-4 h-4" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    {/* Basic Details */}
                    <div>
                        <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">School Details</h5>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="school_name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">School Name</label>
                                <input
                                    type="text"
                                    id="school_name"
                                    name="school_name"
                                    placeholder="Your school's registered name"
                                    value={formik.values.school_name}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                />
                                {formik.errors.school_name && formik.touched.school_name &&
                                    <p className="mt-1 text-xs text-red-500">{formik.errors.school_name}</p>}
                            </div>

                            <div>
                                <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">School Phone Number</label>
                                <input
                                    type="text"
                                    id="phone_number"
                                    name="phone_number"
                                    placeholder="School contact number"
                                    value={formik.values.phone_number}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                />
                                {formik.errors.phone_number && formik.touched.phone_number &&
                                    <p className="mt-1 text-xs text-red-500">{formik.errors.phone_number}</p>}
                            </div>

                            <div className="lg:col-span-2">
                                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">School Category</label>
                                <select
                                    id="industry"
                                    value={formik.values.industry}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                >
                                    <option value="">Select school category</option>
                                    <option value="Education-NurserySchools">Nursery School</option>
                                    <option value="Education-PrimarySchools">Primary School</option>
                                    <option value="Education-SecondarySchools">Secondary School</option>
                                    <option value="Education-TertiaryInstitutions">Tertiary Institution</option>
                                    <option value="Education-OtherEducationalServices">Other Educational Services</option>
                                </select>
                                {formik.errors.industry && formik.touched.industry &&
                                    <p className="mt-1 text-xs text-red-500">{formik.errors.industry}</p>}
                            </div>
                        </div>

                        <div className="mt-6">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">About your School</label>
                            <textarea
                                id="description"
                                value={formik.values.description}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                placeholder="Tell us briefly about your institution..."
                                rows="4"
                                className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                            />
                            {formik.errors.description && formik.touched.description &&
                                <p className="mt-1 text-xs text-red-500">{formik.errors.description}</p>}
                        </div>
                    </div>

                    {/* Financial Management — extra fields */}
                    {wantsFinancial && (
                        <div className="pt-4 border-t border-gray-100">
                            <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Financial Registration Details</h5>
                            <p className="text-xs text-gray-400 mb-4">Required for financial services compliance verification.</p>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="bvn" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                        Owner's BVN
                                        <span className="text-gray-400 font-normal text-xs ml-1">(business officer / signatory)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="bvn"
                                        name="bvn"
                                        placeholder="11-digit BVN"
                                        value={formik.values.bvn}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    />
                                    {formik.errors.bvn && formik.touched.bvn &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.bvn}</p>}
                                </div>

                                <div>
                                    <label htmlFor="registration_type" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Registration Type</label>
                                    <select
                                        id="registration_type"
                                        value={formik.values.registration_type}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    >
                                        <option value="">Select type</option>
                                        <option value="Private_Incorporated">Private Incorporated</option>
                                        <option value="Incorporated_Trustees">Incorporated Trustees</option>
                                        <option value="Business_Name">Business Name</option>
                                        <option value="Free_Zone">Free Zone</option>
                                        <option value="Cooperative_Society">Cooperative Society</option>
                                        <option value="Public_Incorporated">Public Incorporated</option>
                                    </select>
                                    {formik.errors.registration_type && formik.touched.registration_type &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.registration_type}</p>}
                                </div>

                                <div>
                                    <label htmlFor="date_of_registration" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Date of Registration</label>
                                    <input
                                        type="date"
                                        id="date_of_registration"
                                        name="date_of_registration"
                                        value={formik.values.date_of_registration}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    />
                                    {formik.errors.date_of_registration && formik.touched.date_of_registration &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.date_of_registration}</p>}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="pt-6 flex justify-end">
                        {loading ? (
                            <ButtonLoading styles="inline-flex items-center justify-center py-2.5 px-8 border border-transparent text-sm font-medium rounded-full text-white bg-indigo-400 cursor-wait" />
                        ) : (
                            <button
                                type="submit"
                                className="inline-flex items-center justify-center py-2.5 px-8 border border-transparent shadow-md text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all"
                            >
                                Save School Details
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}

export default Information
